import { CloseOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import { Box, Button, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { useContext, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SiteLookupContext from '../context/SiteLookupContext';
import { siteLookupTokens } from '../siteLookupTokens';
import FilterPopover from './FilterPopover';
import { getFilterChipLabel } from './filterChipLabel';
import { FilterCategory, defaultSearchFilters, filterCategories, getActiveCategories, withCategory } from './searchFilters';

const { chip, inputBorder, link, text } = siteLookupTokens.color;

const focusRing = { outline: `2px solid ${siteLookupTokens.color.primary}`, outlineOffset: 2 };

/** The five filters, always visible. Each chip opens the popover of its category, one at a time. */
const FilterChips = () => {
  const { t, i18n } = useTranslation();
  const { appliedFilters, applyFilters, failedRequest, networks, openFilter, pendingRequest, result, retrySearch, setOpenFilter } =
    useContext(SiteLookupContext);

  const popoverId = useId();
  const chipRefs = useRef<Partial<Record<FilterCategory, HTMLButtonElement | null>>>({});

  const activeCategories = getActiveCategories(appliedFilters);
  const isSearching = !!pendingRequest;

  // Focus goes back to the chip once its popover (and its focus trap) is closed, not when the address or radius closes it.
  const focusOnClose = useRef<FilterCategory | null>(null);
  const closeAndRestoreFocus = () => {
    focusOnClose.current = openFilter;
    setOpenFilter(null);
  };

  useEffect(() => {
    if (!openFilter && focusOnClose.current) {
      chipRefs.current[focusOnClose.current]?.focus();
      focusOnClose.current = null;
    }
  }, [openFilter]);

  // Removes the category from the applied filters (never the draft); it stays applied until the search succeeds.
  // The × disappears or is disabled while searching, so the focus moves to the label of the same chip.
  const removeCategory = (category: FilterCategory) => {
    chipRefs.current[category]?.focus();
    void applyFilters(withCategory(appliedFilters, defaultSearchFilters, category));
  };

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
      <Box component="ul" aria-label={t('siteLookup.filters.label')} display="flex" flexWrap="wrap" gap={1} margin={0} padding={0} sx={{ listStyle: 'none' }}>
        {filterCategories.map((category) => {
          const isActive = activeCategories.includes(category);
          const isOpen = openFilter === category;

          return (
            <Box
              component="li"
              key={category}
              display="flex"
              alignItems="stretch"
              maxWidth="100%"
              sx={{
                backgroundColor: isActive ? chip.activeBackground : '#FFFFFF',
                border: '1px solid',
                borderColor: isActive ? chip.activeBorder : inputBorder,
                borderRadius: siteLookupTokens.size.chipRadius,
                boxShadow: isOpen ? `0 0 0 2px ${chip.openRing}` : 'none',
                color: isActive ? chip.activeText : text.primary,
                height: siteLookupTokens.size.chipHeight,
                '&:hover': { borderColor: isActive ? chip.openRing : text.muted },
              }}
            >
              <ButtonBase
                ref={(element) => {
                  chipRefs.current[category] = element;
                }}
                aria-controls={isOpen ? popoverId : undefined}
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                onClick={() => (isOpen ? closeAndRestoreFocus() : setOpenFilter(category))}
                sx={{
                  borderRadius: siteLookupTokens.size.chipRadius,
                  fontSize: 13,
                  fontWeight: 500,
                  gap: 0.25,
                  minWidth: 0,
                  paddingLeft: 1.75,
                  paddingRight: isActive ? 0.5 : 1,
                  '&.Mui-focusVisible': focusRing,
                }}
              >
                <Box component="span" maxWidth={240} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  {getFilterChipLabel(category, appliedFilters, networks.data, t, i18n.language)}
                </Box>
                {isOpen ? <KeyboardArrowUpOutlined aria-hidden sx={{ fontSize: 18 }} /> : <KeyboardArrowDownOutlined aria-hidden sx={{ fontSize: 18 }} />}
              </ButtonBase>
              {isActive && (
                // A sibling of the label button, never nested in it: the label opens the popover, the × removes the filter.
                <ButtonBase
                  aria-label={t(`siteLookup.filters.remove.${category}`)}
                  disabled={isSearching}
                  onClick={() => removeCategory(category)}
                  sx={{
                    borderRadius: siteLookupTokens.size.chipRadius,
                    flexShrink: 0,
                    width: 32,
                    '&:hover': { backgroundColor: chip.activeBorder },
                    '&.Mui-disabled': { color: text.muted },
                    '&.Mui-focusVisible': focusRing,
                  }}
                >
                  <CloseOutlined aria-hidden sx={{ fontSize: 16 }} />
                </ButtonBase>
              )}
            </Box>
          );
        })}
      </Box>

      {!!activeCategories.length && (
        <Button
          disabled={isSearching}
          // Clear all keeps the address and the radius; the filters are removed once the search succeeds.
          onClick={() => void applyFilters(defaultSearchFilters)}
          size="small"
          sx={{ color: link, fontSize: 13, fontWeight: 600, minHeight: siteLookupTokens.size.chipHeight }}
        >
          {t('siteLookup.filters.clearAll')}
        </Button>
      )}

      <Box component="output" display="flex" alignItems="center" gap={1} minWidth={0}>
        {isSearching && (
          <>
            <CircularProgress size={16} aria-hidden />
            <Typography color={text.secondary} fontSize={13}>
              {t('siteLookup.filters.searching')}
            </Typography>
          </>
        )}
      </Box>

      {!!failedRequest && !isSearching && (
        <Box role="alert" display="flex" alignItems="center" gap={0.5} minWidth={0}>
          <Typography color="error" fontSize={13}>
            {result ? t('siteLookup.filters.errorWithPrevious') : t('siteLookup.filters.error')}
          </Typography>
          <Button onClick={retrySearch} size="small" sx={{ color: link, fontWeight: 600 }}>
            {t('siteLookup.filters.retry')}
          </Button>
        </Box>
      )}

      {openFilter && (
        <FilterPopover
          key={openFilter}
          anchorEl={chipRefs.current[openFilter] ?? null}
          category={openFilter}
          id={popoverId}
          onApplied={closeAndRestoreFocus}
          onClose={closeAndRestoreFocus}
        />
      )}
    </Box>
  );
};

export default FilterChips;
